import {
  Datagrid,
  FunctionField,
  List,
  NumberInput,
  SelectInput,
  TextInput,
} from 'react-admin';
import { Alert, Box, Chip, Stack, Typography } from '@mui/material';

import { displayNameFor, formatDate, semanticStateColor } from '@src/components/indexPage';

import { formatMoney } from './format';
import TradeinExpand from './TradeinExpand';
import TradeinListError from './TradeinListError';
import { tradeinTypeLabel } from './TradeinDetailSections';
import { useTradeinConstants } from './useTradeinConstants';
import type { ConstantOption, TradeinRecord } from './types';

const toChoices = (options: ConstantOption[]) =>
  options.map(({ code, displayName }) => ({ id: code, name: displayName }));

const YES_NO = [{ id: 'true', name: 'Yes' }, { id: 'false', name: 'No' }];

const TradeinList = () => {
  const {
    states,
    sources,
    sendbackStates,
    conditions,
    rejectReasons,
    giftcardReasons,
    isFallback,
  } = useTradeinConstants();

  const filters = [
    // A TextInput, not a SearchInput: there is no substring search to offer. The resolver
    // uppercases the input and matches the `uuid_prefix` column for equality, so a partial
    // trade-in number — or a supplier name — matches nothing. (A full UUID is also accepted, on
    // a separate branch that matches `uuid`.)
    <TextInput
      key="uuidPrefix"
      source="uuidPrefix"
      label="Trade-in number"
      placeholder="Full trade-in number, exact match"
      alwaysOn
    />,
    <SelectInput key="state" source="state" label="Status" choices={toChoices(states)} />,
    <SelectInput
      key="tradeinSource"
      source="tradeinSource"
      label="Type"
      choices={sources.map(({ code }) => ({ id: code, name: tradeinTypeLabel(code) }))}
    />,
    <SelectInput key="sendbackState" source="sendbackState" label="Sendback status" choices={toChoices(sendbackStates)} />,
    <TextInput key="email" source="email" label="Email" />,
    <TextInput key="lastName" source="lastName" label="Last name" />,
    <TextInput key="firstName" source="firstName" label="First name" />,
    <NumberInput key="id" source="id" label="Trade-in ID" />,
    <TextInput key="city" source="city" label="City" />,
    <SelectInput key="hasEscalations" source="hasEscalations" label="Has escalations" choices={YES_NO} />,
  ];

  return (
    <>
      {isFallback && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Showing default filter options — live trade-in and sendback states, conditions, and
          reasons couldn&apos;t be loaded, so filtering may be incomplete.
        </Alert>
      )}
      {/* No `sort` prop, and every column below is sortable={false}: the tradeins query takes
          no ordering argument and the resolver is hardcoded to newest-first, so a sort control
          would flip the arrow, refetch, and return identical rows. */}
      <List
        resource="tradeins"
        title="Trade-ins"
        filters={filters}
        perPage={20}
        exporter={false}
        error={<TradeinListError />}
      >
        <Datagrid
          bulkActionButtons={false}
          expand={(
            <TradeinExpand
              states={states}
              conditions={conditions}
              sendbackStates={sendbackStates}
              rejectReasons={rejectReasons}
              giftcardReasons={giftcardReasons}
            />
          )}
          rowClick="expand"
          sx={{ '& .RaDatagrid-rowCell': { verticalAlign: 'middle' } }}
        >
          <FunctionField<TradeinRecord>
            source="uuidPrefix"
            label="Trade-in"
            sortable={false}
            render={tradein => (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: "'SFMono-Regular', Consolas, monospace" }}>
                  {tradein.uuidPrefix ?? '—'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {`#${tradein.id} · ${tradeinTypeLabel(tradein.tradeinStyle)}`}
                </Typography>
              </Box>
            )}
          />
          <FunctionField<TradeinRecord>
            label="Supplier"
            sortable={false}
            render={tradein => (
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                  {tradein.deliveryAddress?.name ?? '—'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {tradein.deliveryAddress?.email ?? ''}
                </Typography>
              </Box>
            )}
          />
          <FunctionField<TradeinRecord>
            label="Status"
            sortable={false}
            render={tradein => (
              <Chip size="small" color={semanticStateColor(tradein.state)} label={displayNameFor(states, tradein.state)} />
            )}
          />
          <FunctionField<TradeinRecord>
            label="Items"
            sortable={false}
            textAlign="right"
            render={tradein => `${tradein.receivedCount ?? 0}/${tradein.initiatedCount ?? 0}`}
          />
          <FunctionField<TradeinRecord>
            label="Value"
            sortable={false}
            textAlign="right"
            render={tradein => (
              <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatMoney(tradein.tradeinAmount, tradein.transactionCurrencyCode)}
              </Typography>
            )}
          />
          <FunctionField<TradeinRecord>
            label="Initiated"
            sortable={false}
            source="initiatedAt"
            render={tradein => (
              <Stack>
                <Typography variant="body2">{formatDate(tradein.initiatedAt)}</Typography>
                {tradein.sendbackState && (
                  <Typography variant="caption" sx={{ color: 'warning.main' }}>In sendback</Typography>
                )}
              </Stack>
            )}
          />
        </Datagrid>
      </List>
    </>
  );
};

export default TradeinList;
